import posthog from 'posthog-js';

// Initialize PostHog with your project API key
export const initPostHog = () => {
  if (typeof window !== 'undefined' && !posthog.__loaded) {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY || '', {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      capture_pageview: false, // We'll handle this manually
      capture_pageleave: true,
      debug: process.env.NODE_ENV === 'development',
      persistence: 'localStorage',
      bootstrap: {
        distinctID: undefined,
        isIdentifiedID: false,
      },
      loaded: (ph) => {
        if (process.env.NODE_ENV === 'production') {
          ph.capture('$web_vitals', {
            $set: { enable_web_vitals: true }
          });
        }
      }
    });
  }
  return posthog;
};

export default posthog;

// Custom event tracking functions
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  posthog.capture(eventName, properties);
};

// Common events
export const events = {
  // Page Events
  PAGE_VIEW: 'page_view',
  SCROLL_DEPTH: 'scroll_depth',
  
  // User Interaction Events
  BUTTON_CLICK: 'button_click',
  LINK_CLICK: 'link_click',
  FORM_SUBMIT: 'form_submit',
  FORM_ERROR: 'form_error',
  
  // Feature Page Events
  FEATURE_VIEW: 'feature_view',
  FEATURE_INTERACTION: 'feature_interaction',
  
  // Pricing Events
  PRICING_VIEW: 'pricing_view',
  PLAN_SELECTED: 'plan_selected',
  BILLING_CYCLE_CHANGED: 'billing_cycle_changed',
  
  // Contact Events
  CONTACT_FORM_START: 'contact_form_start',
  CONTACT_FORM_COMPLETE: 'contact_form_complete',
  
  // Navigation Events
  MENU_OPEN: 'menu_open',
  MENU_CLOSE: 'menu_close',
  NAVIGATION_CLICK: 'navigation_click',
  
  // Content Interaction Events
  VIDEO_PLAY: 'video_play',
  VIDEO_COMPLETE: 'video_complete',
  DOCUMENT_DOWNLOAD: 'document_download',
  
  // Error Events
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',
};

// Properties to track with events
export const properties = {
  // Page Properties
  page: {
    name: 'page_name',
    url: 'page_url',
    referrer: 'referrer',
  },
  
  // User Properties
  user: {
    type: 'user_type',
    role: 'user_role',
    status: 'user_status',
  },
  
  // Interaction Properties
  interaction: {
    type: 'interaction_type',
    location: 'interaction_location',
    value: 'interaction_value',
  },
  
  // Feature Properties
  feature: {
    name: 'feature_name',
    category: 'feature_category',
    action: 'feature_action',
  },
  
  // Error Properties
  error: {
    type: 'error_type',
    message: 'error_message',
    code: 'error_code',
  },
};

// Utility function to track page views with additional data
export const trackPageView = (pageName: string, additionalData?: Record<string, any>) => {
  trackEvent(events.PAGE_VIEW, {
    [properties.page.name]: pageName,
    [properties.page.url]: window.location.href,
    [properties.page.referrer]: document.referrer,
    ...additionalData,
  });
};

// Utility function to track feature interactions
export const trackFeatureInteraction = (
  featureName: string,
  action: string,
  additionalData?: Record<string, any>
) => {
  trackEvent(events.FEATURE_INTERACTION, {
    [properties.feature.name]: featureName,
    [properties.feature.action]: action,
    ...additionalData,
  });
};

// Utility function to track errors
export const trackError = (
  errorType: string,
  errorMessage: string,
  errorCode?: string,
  additionalData?: Record<string, any>
) => {
  trackEvent(events.ERROR_OCCURRED, {
    [properties.error.type]: errorType,
    [properties.error.message]: errorMessage,
    [properties.error.code]: errorCode,
    ...additionalData,
  });
}; 